import _ from 'lodash';
import React, { Fragment, ReactElement } from 'react';

import i18n from 'i18n';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faQuestionCircle,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import {
  ColumnDescription,
  ColumnFormatter,
  HeaderFormatter,
  SortOrder,
} from 'react-bootstrap-table-next';
import { NameWithIdColumn } from './NameWithIdColumn';
import { LabelValueColumn } from './LabelValueColumn';
import { SelectOptions } from '../commonType';
import { SelectableColumn } from './SelectableColumn';
import { SelectableHeader } from './SelectableHeader';
import { Trans } from 'react-i18next';
import { Status } from 'components/Status/Status';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import styles from './tableColumn.module.scss';
import classNames from 'classnames/bind';
import { Link } from 'react-router-dom';

const cx = classNames.bind(styles);

export type SortDescriptor = [
  {
    readonly order: SortOrder;
    readonly dataField: any;
  },
];

export type ColumnDefinition = Omit<
  ColumnDescription,
  'formatter' | 'headerFormatter'
>;

function translatedHeader(
  column: ColumnDescription,
  columnIndex: number,
  { sortElement },
) {
  const header = i18n.t<string>(column.text);
  return (
    <Fragment>
      {header}
      {sortElement}
    </Fragment>
  );
}

export function renderColumn<R extends object = any, C = any, E = any>(
  column: ColumnDefinition,
  formatter: ColumnFormatter<R, E, C> | undefined = undefined,
  headerFormatter: HeaderFormatter<R> | undefined = translatedHeader,
): ColumnDescription {
  const columnProps: ColumnDescription<R> = {
    ...column,
    formatter: formatter,
    headerFormatter,
  };
  return _.omitBy(columnProps, _.isNil) as ColumnDescription;
}

export function sortableColumn(
  dataField: string,
  text: string,
  sort: boolean = true,
): ColumnDefinition {
  return {
    sort,
    text,
    dataField,
  };
}

export function checkedFormatter(cell: boolean) {
  return (
    <Fragment>
      {cell ? (
        <FontAwesomeIcon icon={faCheck} />
      ) : (
        <FontAwesomeIcon icon={faTimes} />
      )}
    </Fragment>
  );
}

export function withStyles(
  column: ColumnDefinition,
  styles: { [key: string]: string },
) {
  return {
    ...column,
    classes: () => styles[column.dataField],
    headerClasses: () => styles[column.dataField],
  };
}

export function selectableHeaderFormatter(
  column: ColumnDefinition,
  _2,
  { sortElement },
) {
  const formatExtraData = column.formatExtraData;
  if (!formatExtraData) {
    return (
      <div>
        {column.text}
        {sortElement}
      </div>
    );
  }
  const selectedRows = _.defaultTo(formatExtraData.selectedRows, []);
  return (
    <SelectableHeader
      allSelected={formatExtraData.allSelected}
      selectedCount={selectedRows.length}
      onSelectPage={formatExtraData.onSelectPage}
    >
      {column.text}
      {sortElement}
    </SelectableHeader>
  );
}

export function headerWithIconFormatter(
  column: ColumnDefinition,
  _1,
  { sortElement },
) {
  const dataField = column.dataField;
  const formatExtraData = column.formatExtraData;
  if (!formatExtraData) {
    return (
      <div>
        {column.text}
        {sortElement}
      </div>
    );
  }

  return (
    <div className={cx(styles.tableHeaderHint, formatExtraData.className)}>
      {column.text}
      <IconWithTooltip
        key={dataField}
        icon={formatExtraData.icon ? formatExtraData.icon : faQuestionCircle}
        tooltipProps={{
          id: dataField,
          tooltip: formatExtraData.hint,
        }}
      />
      {sortElement}
    </div>
  );
}

export function selectableColumnFormatter<T extends { id: string | number }>(
  data: any,
  record: T,
  rowIndex: number,
  formatExtraData: {
    selectedRows: (string | number)[];
    onSelect: (id: string | number) => void;
    columnFormatter: (
      data: any,
      record: T,
      rowIndex: number,
      extraData: any,
    ) => ReactElement;
    enableSelect: (id: string | number) => boolean;
  },
): ReactElement {
  const enableSelect = formatExtraData.enableSelect
    ? formatExtraData.enableSelect
    : (_1: string | number) => true;
  return (
    <SelectableColumn
      id={record.id}
      isSelected={formatExtraData.selectedRows.includes(record.id)}
      onSelect={formatExtraData.onSelect}
      enableSelect={enableSelect}
    >
      {formatExtraData.columnFormatter(data, record, rowIndex, formatExtraData)}
    </SelectableColumn>
  );
}

export function getNameIdColumn<
  T extends { id: string | number; name: string },
>(
  column: ColumnDefinition,
  getClickUrl?: (record: T) => string | undefined,
  isActive?: (record: T) => boolean,
) {
  return renderColumn(
    {
      ...column,
      formatExtraData: { getClickUrl, isActive },
    },
    function nameWithIdFormatter<
      T extends { id: string | number; name: string },
    >(
      _1,
      record: T,
      rowIndex: number,
      formatExtraData?: {
        getClickUrl?: (record: T) => string | undefined;
        isActive?: (record: T) => boolean;
      },
    ): ReactElement {
      const clickUrl = formatExtraData!.getClickUrl
        ? formatExtraData!.getClickUrl(record)
        : undefined;
      const isActive = formatExtraData!.isActive
        ? formatExtraData!.isActive(record)
        : true;
      return (
        <NameWithIdColumn
          id={record.id}
          name={record.name}
          clickUrl={clickUrl}
          isActive={isActive}
        />
      );
    },
  );
}

export function getLinkColumn(
  column: ColumnDefinition,
  getClickUrl?: (record: any) => string | undefined,
) {
  return renderColumn(
    {
      ...column,
      formatExtraData: { getClickUrl },
    },
    function linkFormatter(
      field: string | number,
      record: any,
      _2,
      formatExtraData?: {
        getClickUrl: (record: any) => string | undefined;
      },
    ): ReactElement {
      if (!formatExtraData) {
        return <>{field}</>;
      }

      const clickUrl = formatExtraData.getClickUrl(record);
      if (!clickUrl) {
        return <>{field}</>;
      }

      return <Link to={clickUrl}>{field}</Link>;
    },
  );
}

export function getStringArrayColumn(
  column: ColumnDefinition,
  valueDecorator?: (value: string | number) => string,
) {
  return renderColumn(column, (strings: string[]): ReactElement => {
    if (!strings) return <div />;
    const renderStrings = () =>
      strings.map((str, index) => (
        <div key={index}>{valueDecorator ? valueDecorator(str) : str}</div>
      ));
    return <div>{renderStrings()}</div>;
  });
}

export function getCheckedColumn(column: ColumnDefinition) {
  return renderColumn(column, checkedFormatter);
}

export function getLabelValueColumn(
  column: ColumnDefinition,
  getOptions: (record: any) => SelectOptions[],
) {
  return renderColumn(
    {
      ...column,
      formatExtraData: { getOptions },
    },
    (
      _1,
      record: any,
      _3,
      formatExtraData?: {
        getOptions: (record: any) => SelectOptions[];
      },
    ): ReactElement => {
      return <LabelValueColumn options={formatExtraData!.getOptions(record)} />;
    },
  );
}

export function getDateRangeColumn(
  column: ColumnDefinition,
  getDateRange: (record: any) => { startDate: string; endDate: string },
) {
  return renderColumn(
    {
      ...column,
      formatExtraData: { getDateRange },
    },
    (
      _1,
      record: any,
      _3,
      formatExtraData?: {
        getDateRange: (record: any) => { startDate: string; endDate: string };
      },
    ): ReactElement => {
      const { startDate, endDate } = formatExtraData!.getDateRange(record);
      return (
        <div style={{ width: 'max-content' }}>
          <Trans i18nKey='common.duration'>
            {{ start: startDate }}
            <br />
            to
            <br />
            {{ end: endDate }}
          </Trans>
        </div>
      );
    },
  );
}

export function getStatusDesColumn(
  column: ColumnDefinition,
  getStatusDesData: (
    record: any,
  ) => { des: string; color?: string; style?: React.CSSProperties }[],
) {
  return renderColumn(
    {
      ...column,
      formatExtraData: { getStatusDesData },
    },
    (
      _1,
      record: any,
      _3,
      formatExtraData?: {
        getStatusDesData: (
          record: any,
        ) => { des: string; color: string; style?: React.CSSProperties }[];
      },
    ): ReactElement => {
      const datas = formatExtraData!.getStatusDesData(record);
      if (datas.length === 0) {
        return <div>{i18n.t<string>('common.labels.noData')}</div>;
      }

      const bubbles = datas.map(({ des, color, style }) => (
        <Status key={des} label={des} color={color} style={style} />
      ));
      return <div className={styles.bubbles}>{bubbles}</div>;
    },
  );
}
